// workspace-panel.ts — Pastille de navigation + volet latéral.
//
// Le volet s'insère ENTRE la navigation et le contenu, il ne le recouvre pas :
// rien n'est masqué, la page reste utilisable pendant la consultation. C'est
// ce qui le rend acceptable sur une page de saisie, contrairement à un panneau
// flottant.
//
// Il replie automatiquement la navigation (224 → 60 px) pour récupérer de la
// place, et **restaure son état antérieur à la fermeture** : le repli est un
// effet de bord du volet, pas un réglage que l'utilisateur aurait demandé.
//
// Pas de volet sous 768 px : à 375 px, 240 px de volet n'ont pas de sens — la
// pastille mène alors directement à `#workspace`.

import * as store from './workspace-store.js';
import { landingHash, openTileParams } from './page-workspace.js';
import { renderTile } from './workspace-tiles.js';

const MOBILE_MAX = 768;
const PANEL_ID = 'workspace-panel';

let open = false;
let navWasCollapsed = false;

function isMobile(): boolean {
  return window.innerWidth <= MOBILE_MAX;
}

function panelEl(): HTMLElement | null {
  return document.getElementById(PANEL_ID);
}

function sidebarEl(): HTMLElement | null {
  return document.getElementById('sidebar');
}

// ── Badge ──────────────────────────────────────────────────────────────────

/**
 * Le badge compte l'URGENT, pas le total : les actions dont je suis le
 * prochain à agir. Un badge à 142 ne dirait rien et deviendrait du bruit
 * permanent. **Aucun badge à zéro** — c'est ce qui lui garde son pouvoir
 * d'alerte.
 */
function paintBadge(): void {
  const host = document.getElementById('ws-trigger');
  if (!host) return;
  const existing = host.querySelector('.nav-badge');
  const n = store.badgeCount();
  if (!n) {
    existing?.remove();
    return;
  }
  if (existing) {
    existing.textContent = String(n);
    return;
  }
  const badge = document.createElement('span');
  badge.className = 'nav-badge';
  badge.textContent = String(n);
  host.appendChild(badge);
}

// ── Volet ──────────────────────────────────────────────────────────────────

function paintPanel(): void {
  const el = panelEl();
  if (!el) return;
  const tiles = store.getTiles();
  el.innerHTML = `
    <div class="ws-panel-h">
      <span class="ws-panel-t">${esc(t('workspace.title'))}</span>
      <a class="ws-panel-all" href="#workspace">${esc(t('workspace.seeAll'))}</a>
      <button type="button" class="ws-panel-x" id="ws-panel-close"
        aria-label="${esc(t('common.close'))}">×</button>
    </div>
    <div class="ws-panel-b">
      ${
        tiles.length
          ? tiles
              .map((tile) => renderTile(tile, 'compact', { flash: store.isFlashing(tile.id) }))
              .join('')
          : `<p class="ws-empty">${esc(t('workspace.empty'))}</p>`
      }
    </div>`;
  document.getElementById('ws-panel-close')?.addEventListener('click', () => setOpen(false));
}

function setOpen(next: boolean): void {
  // Sur mobile, la pastille est un lien direct vers la page : pas de volet.
  if (isMobile()) {
    router.navigate('#workspace');
    return;
  }
  open = next;
  const sidebar = sidebarEl();
  const trigger = document.getElementById('ws-trigger');
  document.body.classList.toggle('ws-panel-open', open);
  trigger?.setAttribute('aria-expanded', String(open));
  if (open) {
    navWasCollapsed = !!sidebar?.classList.contains('collapsed');
    sidebar?.classList.add('collapsed');
    void store.load().then(paintPanel);
    paintPanel();
  } else if (!navWasCollapsed) {
    // Restaure le réglage de l'utilisateur : certains ont choisi leur menu
    // déplié et doivent le retrouver.
    sidebar?.classList.remove('collapsed');
  }
}

// ── Câblage ────────────────────────────────────────────────────────────────

export function initWorkspacePanel(): void {
  const trigger = document.getElementById('ws-trigger');
  if (!trigger) return;

  store.wireInvalidation();
  store.subscribe(() => {
    paintBadge();
    if (open) paintPanel();
  });

  trigger.addEventListener('click', (ev) => {
    ev.preventDefault();
    setOpen(!open);
  });

  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && open) setOpen(false);
  });

  // Le volet délègue ses clics : atterrissage identique à celui de la page,
  // même `landing` publié par le serveur.
  panelEl()?.addEventListener('click', (ev) => {
    const el = ev.target as HTMLElement | null;
    const card = el?.closest<HTMLElement>('.ws-tile');
    if (!card?.dataset.tile) return;
    if (el?.closest('.ws-help')) return;
    const gear = el?.closest<HTMLElement>('.ws-gear');
    if (gear?.dataset.tile) return openTileParams(Number(gear.dataset.tile));
    const tile = store.getTiles().find((tj) => tj.id === Number(card.dataset.tile));
    if (tile) router.navigate(landingHash(tile));
  });

  // Chargement au démarrage : le volet doit s'ouvrir sans latence, donc les
  // données sont déjà là quand l'utilisateur clique. Échec silencieux — un
  // badge absent vaut mieux qu'une erreur au démarrage de l'application.
  void store.load().catch((e) => safeWarn('[workspace-panel] preload', e));
}
